// src/components/admin/SalesUsersModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { X, Users, Trash2, RefreshCw, CheckCircle, XCircle, Mail, Edit } from "lucide-react";
import { useTranslation } from "react-i18next";
import { adminService } from "../../services/admin.service";

const empty = () => ({
  fullName: "",
  phoneNumber: "",
  smtpEmail: "",
  smtpPassword: "",
  smtpHost: "",
  smtpPort: "",
  isActive: true,
});

const SalesUsersModal = ({ onClose }) => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const timerRef = useRef(null);

  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState(empty());

  const showMessage = (type, text) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setMessage({ type, text });
    timerRef.current = setTimeout(() => setMessage({ type: "", text: "" }), 3500);
  };

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const data = await adminService.getSalesUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      showMessage("error", e?.message || t("messages.networkError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUsers(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      fullName: user.fullName || "",
      phoneNumber: user.phoneNumber || "",
      smtpEmail: user.smtpEmail || "",
      smtpPassword: "",
      smtpHost: "",
      smtpPort: "",
      isActive: user.isActive,
    });
  };

  const cancelEdit = () => {
    setEditingUser(null);
    setForm(empty());
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const dto = {
        fullName: form.fullName || undefined,
        phoneNumber: form.phoneNumber || undefined,
        smtpEmail: form.smtpEmail || undefined,
        smtpPassword: form.smtpPassword || undefined,
        smtpHost: form.smtpHost || undefined,
        smtpPort: form.smtpPort ? Number(form.smtpPort) : undefined,
        isActive: form.isActive,
      };
      await adminService.updateSalesUser(editingUser.id, dto);
      showMessage("success", t("admin.salesUsers.updateSuccess"));
      cancelEdit();
      await fetchUsers();
    } catch (e) {
      showMessage("error", e?.message || t("admin.salesUsers.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("admin.salesUsers.deleteConfirm"))) return;
    try {
      await adminService.deleteSalesUser(id);
      showMessage("success", t("admin.salesUsers.deleteSuccess"));
      await fetchUsers();
    } catch (e) {
      showMessage("error", e?.message || t("admin.salesUsers.deleteError"));
    }
  };

  const canSave = useMemo(
    () => !!form.fullName?.trim() && !saving,
    [form.fullName, saving]
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-md p-4">
      <div className="relative w-full max-w-4xl max-h-[90vh] bg-white/10 backdrop-blur-2xl border border-white/20 shadow-[0_0_40px_rgba(255,0,255,0.3)] rounded-2xl overflow-hidden">
        <div className="p-5 bg-gradient-to-r from-purple-600 to-pink-700 flex items-center gap-3">
          <button
            onClick={onClose}
            className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-full
                       bg-black/40 hover:bg-black/70 border border-white/30 text-white transition"
            aria-label={t("common.close")}
          >
            <X className="w-5 h-5" />
          </button>

          <Users className="w-6 h-6 text-white flex-shrink-0" />
          <h2 className="text-2xl font-bold text-white flex-1">{t("admin.salesUsers.title")}</h2>

          <button
            onClick={fetchUsers}
            disabled={loading || saving}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 border border-white/20 text-white
                       flex items-center gap-2 disabled:opacity-40 flex-shrink-0"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            {t("common.refresh")}
          </button>
        </div>

        <div className="p-6 text-white overflow-y-auto max-h-[calc(90vh-90px)]">
          {message.text && (
            <div className={`mb-5 p-3 rounded-lg text-center text-sm ${
              message.type === "success"
                ? "bg-green-500/20 border border-green-400 text-green-200"
                : "bg-red-500/20 border border-red-400 text-red-200"
            }`}>
              {message.text}
            </div>
          )}

          {/* ── Edit form ── */}
          {editingUser && (
            <div className="mb-6 p-5 bg-white/10 border border-white/10 rounded-xl backdrop-blur-lg shadow-inner">
              <h3 className="text-lg font-semibold mb-4">
                {t("admin.salesUsers.editTitle")} — <span className="text-pink-300">{editingUser.email}</span>
              </h3>

              <form onSubmit={handleUpdate} className="space-y-4">
                {/* Full name */}
                <div>
                  <label className="text-sm text-gray-200">{t("admin.createSalesUser.fullNameLabel")}</label>
                  <input
                    type="text"
                    value={form.fullName}
                    onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))}
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                               placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder={t("admin.createSalesUser.fullNamePlaceholder")}
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="text-sm text-gray-200">{t("admin.createSalesUser.phoneLabel")}</label>
                  <input
                    type="text"
                    value={form.phoneNumber}
                    onChange={(e) => setForm((p) => ({ ...p, phoneNumber: e.target.value }))}
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                               placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder={t("admin.createSalesUser.phonePlaceholder")}
                  />
                </div>

                {/* SMTP email */}
                <div>
                  <label className="text-sm text-gray-200">{t("admin.createSalesUser.smtpEmailLabel")}</label>
                  <input
                    type="email"
                    value={form.smtpEmail}
                    onChange={(e) => setForm((p) => ({ ...p, smtpEmail: e.target.value }))}
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                               placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder={t("admin.createSalesUser.smtpEmailPlaceholder")}
                  />
                </div>

                {/* SMTP password */}
                <div>
                  <label className="text-sm text-gray-200">
                    {t("admin.createSalesUser.smtpPasswordLabel")}
                    <span className="text-gray-400 text-xs ml-1">({t("admin.salesUsers.leaveBlankToKeep")})</span>
                  </label>
                  <input
                    type="password"
                    value={form.smtpPassword}
                    onChange={(e) => setForm((p) => ({ ...p, smtpPassword: e.target.value }))}
                    className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                               placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                    placeholder="••••••••"
                  />
                </div>

                {/* SMTP host + port */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="col-span-2">
                    <label className="text-sm text-gray-200">{t("admin.createSalesUser.smtpHostLabel")}</label>
                    <input
                      type="text"
                      value={form.smtpHost}
                      onChange={(e) => setForm((p) => ({ ...p, smtpHost: e.target.value }))}
                      className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                                 placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                      placeholder={t("admin.createSalesUser.smtpHostPlaceholder")}
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-200">{t("admin.createSalesUser.smtpPortLabel")}</label>
                    <input
                      type="number"
                      value={form.smtpPort}
                      onChange={(e) => setForm((p) => ({ ...p, smtpPort: e.target.value }))}
                      className="w-full mt-1 py-2 px-3 rounded-lg bg-white/10 border border-white/20 text-white
                                 placeholder-gray-300 focus:ring-2 focus:ring-purple-500 outline-none"
                      placeholder="587"
                    />
                  </div>
                </div>

                {/* isActive */}
                <label className="flex items-center gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!form.isActive}
                    onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                    className="w-5 h-5"
                  />
                  <span className="text-sm text-gray-200">{t("admin.productForm.activeLabel")}</span>
                </label>

                <div className="flex gap-3 pt-2">
                  <button
                    type="submit"
                    disabled={!canSave}
                    className="flex-1 bg-gradient-to-r from-purple-500 to-pink-500 py-2 rounded-lg font-semibold
                               hover:opacity-90 disabled:opacity-40"
                  >
                    {saving ? t("admin.salesUsers.updating") : t("admin.salesUsers.update")}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    disabled={saving}
                    className="flex-1 bg-gray-600 py-2 rounded-lg hover:bg-gray-700 font-semibold"
                  >
                    {t("common.cancel")}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ── Users list ── */}
          {loading ? (
            <div className="py-10 text-center text-gray-200">{t("common.loading")}</div>
          ) : users.length === 0 ? (
            <div className="py-14 text-center text-gray-300">{t("admin.salesUsers.noUsers")}</div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {users.map((user) => (
                <div
                  key={user.id}
                  className={`bg-white/10 border rounded-xl p-4 backdrop-blur-lg shadow-xl transition ${
                    editingUser?.id === user.id ? "border-purple-400/60" : "border-white/20"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <p className="font-bold text-pink-200 text-lg truncate">{user.fullName}</p>
                      <p className="text-sm text-gray-300 truncate">{user.email}</p>
                      {user.phoneNumber && (
                        <p className="text-xs text-gray-400 mt-0.5">{user.phoneNumber}</p>
                      )}
                    </div>
                    <span className={`px-2 py-1 rounded text-xs border flex-shrink-0 ${
                      user.isActive
                        ? "bg-green-500/15 border-green-400/40 text-green-200"
                        : "bg-gray-500/15 border-gray-400/40 text-gray-200"
                    }`}>
                      {user.isActive ? t("common.active") : t("common.inactive")}
                    </span>
                  </div>

                  <div className="flex items-center gap-2 text-sm mb-4 p-2 bg-white/5 rounded-lg">
                    <Mail className="w-4 h-4 text-blue-300 flex-shrink-0" />
                    <span className="text-gray-300 truncate flex-1">
                      {user.smtpEmail || t("admin.salesUsers.noSmtp")}
                    </span>
                    {user.hasSmtpConfigured ? (
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => openEdit(user)}
                      disabled={saving}
                      className="flex-1 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold
                                 flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      <Edit className="w-4 h-4" />
                      {t("common.edit")}
                    </button>
                    <button
                      onClick={() => handleDelete(user.id)}
                      disabled={saving}
                      className="flex-1 py-2 rounded-lg bg-red-600/70 hover:bg-red-700 text-white text-sm font-semibold
                                 flex items-center justify-center gap-2 transition disabled:opacity-50"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t("common.delete")}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SalesUsersModal;
